import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Scale, Moon, Navigation, CalendarDays, ArrowUpRight } from "lucide-react";

// Two-owner palette — blue for the first owner, violet for the second.
const TONES = [
  { text: "text-[#4DA6FF]", bar: "bg-[#007AFF]", dot: "bg-[#4DA6FF]", soft: "bg-[#007AFF]/10 border-[#007AFF]/30" },
  { text: "text-[#A78BFA]", bar: "bg-[#8B5CF6]", dot: "bg-[#A78BFA]", soft: "bg-[#8B5CF6]/10 border-[#8B5CF6]/30" },
];

function fmtNum(n) {
  return Number(n) % 1 ? Number(n).toFixed(1) : String(Number(n));
}

// One metric = two horizontal bars (owner A vs owner B) scaled to their max.
function MetricBars({ icon: Icon, label, owners, values, t }) {
  const max = Math.max(values[0], values[1], 1);
  const leadIdx = values[0] === values[1] ? -1 : (values[0] > values[1] ? 0 : 1);
  const gap = Math.abs(values[0] - values[1]);
  return (
    <div data-testid={`usage-metric-${label}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[10px] font-mono tracking-widest text-[#94A3B8]">
          <Icon className="w-3.5 h-3.5" /> {label}
        </div>
        {leadIdx >= 0 && gap > 0 && (
          <span className={`text-[10px] font-mono ${TONES[leadIdx].text}`}>
            {owners[leadIdx].name.split(" ")[0]} +{fmtNum(gap)}
          </span>
        )}
      </div>
      <div className="space-y-1.5">
        {owners.map((o, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-12 shrink-0 text-[11px] text-[#CBD5E1] truncate text-right">{o.name.split(" ")[0]}</span>
            <div className="flex-1 h-4 rounded-full bg-[#050A14] border border-[#1E293B] overflow-hidden">
              <div
                className={`${TONES[i].bar} h-full rounded-full`}
                style={{ width: `${(values[i] / max) * 100}%`, transition: "width 700ms cubic-bezier(0.22,1,0.36,1)" }}
              />
            </div>
            <span className={`w-9 shrink-0 text-right font-display font-bold text-sm tabular-nums ${TONES[i].text}`}>{fmtNum(values[i])}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function UsageBalanceWidget() {
  const t = useT();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const year = new Date().getFullYear();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/intelligence/usage-split?year=${year}`);
        setData(data);
      } catch { /* widget is non-critical — stay silent */ }
      setLoading(false);
    })();
  }, [year]);

  if (loading) {
    return <div className="ledger-card p-5 h-56 animate-pulse" data-testid="usage-balance-loading" />;
  }

  const owners = (data?.owners || []).slice(0, 2);
  if (owners.length < 2) return null;

  const total = owners.reduce((s, o) => s + (o.total_days || 0), 0);
  const leftPct = total > 0 ? (owners[0].total_days / total) * 100 : 50;
  const shared = data?.shared_days_count || 0;
  const diff = Math.abs((owners[0].total_days || 0) - (owners[1].total_days || 0));
  const leader = owners[0].total_days === owners[1].total_days
    ? null
    : (owners[0].total_days > owners[1].total_days ? owners[0] : owners[1]);

  return (
    <div className="ledger-card p-5 sm:p-6 relative overflow-hidden" data-testid="usage-balance-widget">
      <div className="absolute inset-0 grid-lines opacity-30" />
      <div className="relative">
        <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-[#34D399] flex items-center gap-1.5">
              <Scale className="w-3.5 h-3.5" /> {t("dashboard.usage.label")}
            </div>
            <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-1">
              {t("dashboard.usage.title", { year })}
            </h3>
            <p className="text-xs text-[#94A3B8] mt-1">{t("dashboard.usage.subtitle")}</p>
          </div>
          <button
            onClick={() => navigate("/app/intelligence")}
            data-testid="usage-balance-more-btn"
            className="text-xs text-[#4DA6FF] hover:underline flex items-center gap-1 whitespace-nowrap"
          >
            {t("dashboard.usage.viewMore")} <ArrowUpRight className="w-3 h-3" />
          </button>
        </div>

        {total === 0 ? (
          <div className="text-center text-sm text-[#94A3B8] py-8" data-testid="usage-balance-empty">
            {t("dashboard.usage.empty")}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
            {/* Headline balance */}
            <div className="lg:col-span-2">
              <div className="flex items-end gap-6 mb-4">
                {owners.map((o, i) => (
                  <div key={i} className="flex-1" data-testid={`usage-owner-${o.name}`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className={`w-2.5 h-2.5 rounded-full ${TONES[i].dot}`} />
                      <span className="text-sm font-medium truncate">{o.name.split(" ")[0]}</span>
                    </div>
                    <div className={`font-display text-4xl font-bold tracking-tight stat-number ${TONES[i].text}`}>{fmtNum(o.total_days)}</div>
                    <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("dashboard.usage.credited")}</div>
                  </div>
                ))}
              </div>
              <div className="h-3 rounded-full overflow-hidden flex bg-[#050A14] border border-[#1E293B]" data-testid="usage-balance-bar">
                <div className={TONES[0].bar} style={{ width: `${leftPct}%`, transition: "width 700ms ease" }} />
                <div className={TONES[1].bar} style={{ width: `${100 - leftPct}%`, transition: "width 700ms ease" }} />
              </div>
              <div className="flex items-center justify-between mt-2 text-[11px] font-mono text-[#94A3B8]">
                <span data-testid="usage-balance-leader">
                  {leader
                    ? t("dashboard.usage.ahead", { name: leader.name.split(" ")[0], n: fmtNum(diff) })
                    : t("dashboard.usage.even")}
                </span>
                {shared > 0 && (
                  <span>{t(shared === 1 ? "dashboard.usage.sharedOne" : "dashboard.usage.shared", { n: shared })}</span>
                )}
              </div>
            </div>

            {/* Comparison bar graphs */}
            <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-5 sm:border-l sm:border-[#1E293B] sm:pl-6">
              <MetricBars icon={Moon} label={t("dashboard.usage.nights")} owners={owners} t={t}
                values={[owners[0].dock_overnight_days || 0, owners[1].dock_overnight_days || 0]} />
              <MetricBars icon={Navigation} label={t("dashboard.usage.trips")} owners={owners} t={t}
                values={[owners[0].day_trip_days || 0, owners[1].day_trip_days || 0]} />
              <MetricBars icon={CalendarDays} label={t("dashboard.usage.credited")} owners={owners} t={t}
                values={[owners[0].total_days || 0, owners[1].total_days || 0]} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
